import dayjs from 'dayjs';
import {
  IcoApple,
  IcoCalendar,
  IcoDownload,
  IcoGoogle,
  IcoOutlook,
  IcoYahoo,
} from '@components/icons';
import { useConfiguration } from '@components/router/session-context';
import { Course, StudentMeeting } from 'server/types';
import { URLS, toQueryString } from 'shared/urls';
import { toISO8601 } from 'shared/dateutil';
import { useRouteParams } from '@components/router';
import { useIntl } from 'shared/intl/use-intl';
import { Dropdown, MenuItem } from '@components/dropdown';

type Props = {
  meeting: Pick<StudentMeeting, 'id' | 'title' | 'scheduledAt' | 'durationMinutes'>;
};

type MeetingInfo = {
  meeting: Props['meeting'];
  course: Pick<Course, 'id' | 'title'>;
  domain: string;
};

function getMeetingDetails({ meeting, course, domain }: MeetingInfo) {
  const start = dayjs(meeting.scheduledAt);
  const end = start.add(meeting.durationMinutes, 'minutes');
  const description = `Link: ${URLS.student.meeting({ course, domain, meeting })}`;
  return { start, end, description };
}

function buildGoogleCalendarLink(info: MeetingInfo) {
  if (!info.meeting.scheduledAt) {
    return;
  }
  const { start, end, description } = getMeetingDetails(info);
  const params = toQueryString({
    text: info.meeting.title,
    details: description,
    dates: `${toISO8601(start.toDate())}/${toISO8601(end.toDate())}`,
    sf: true,
  });
  return `https://www.google.com/calendar/render?action=TEMPLATE&${params}`;
}

function buildOutlookLink(info: MeetingInfo, host: string) {
  if (!info.meeting.scheduledAt) {
    return;
  }
  const { start, end, description } = getMeetingDetails(info);
  const params = toQueryString({
    rru: 'addevent',
    subject: info.meeting.title,
    body: description,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    path: '/calendar/action/compose',
  });
  return `https://${host}/calendar/0/action/compose?${params}`;
}

function buildYahooLink(info: MeetingInfo) {
  if (!info.meeting.scheduledAt) {
    return;
  }
  const { start, description } = getMeetingDetails(info);
  const hours = Math.floor(info.meeting.durationMinutes / 60);
  const mins = info.meeting.durationMinutes % 60;
  const dur = `${String(hours).padStart(2, '0')}${String(mins).padStart(2, '0')}`;
  const params = toQueryString({
    v: 60,
    title: info.meeting.title,
    desc: description,
    st: toISO8601(start.toDate()),
    dur,
  });
  return `https://calendar.yahoo.com/?${params}`;
}

export function AddToCalendarButtons({ meeting }: Props) {
  const intl = useIntl();
  const configuration = useConfiguration();
  const { courseId, courseSlug } = useRouteParams();

  if (!meeting.scheduledAt) {
    return null;
  }

  const info: MeetingInfo = {
    course: { id: courseId, title: courseSlug },
    meeting,
    domain: configuration?.tenant.domain || '',
  };

  const googleLink = buildGoogleCalendarLink(info);
  const outlookLiveLink = buildOutlookLink(info, 'outlook.live.com');
  const office365Link = buildOutlookLink(info, 'outlook.office.com');
  const yahooLink = buildYahooLink(info);
  const icsLink = `/api/meeting/ical/${courseId}/${meeting.id}`;

  return (
    <div class="flex justify-center">
      <Dropdown
        triggerClass="border-gray-300 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-600 hover:bg-gray-50 btn gap-2"
        renderMenu={() => (
          <div class="flex flex-col">
            <MenuItem href={icsLink} download={meeting.title} regularCase>
              <IcoApple class="h-4 w-4 opacity-75" />
              {intl('Apple Calendar')}
            </MenuItem>
            <MenuItem href={googleLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoGoogle class="h-4 w-4 opacity-75" />
              {intl('Google Calendar')}
            </MenuItem>
            <MenuItem href={outlookLiveLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoOutlook class="h-4 w-4 opacity-75" />
              {intl('Outlook.com')}
            </MenuItem>
            <MenuItem href={office365Link} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoOutlook class="h-4 w-4 opacity-75" />
              {intl('Office 365')}
            </MenuItem>
            <MenuItem href={yahooLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoYahoo class="h-4 w-4 opacity-75" />
              {intl('Yahoo Calendar')}
            </MenuItem>
            <MenuItem href={icsLink} download={meeting.title} regularCase>
              <IcoDownload class="h-4 w-4 opacity-75" />
              {intl('Download .ics file')}
            </MenuItem>
          </div>
        )}
      >
        <IcoCalendar class="w-4 h-4 opacity-75" />
        {intl('Add to Calendar')}
      </Dropdown>
    </div>
  );
}
